/* ============================================================
   ELITE COMPETITOR ACADEMY — shared behaviour
   Guarded so it runs once even if several sections request it.
   Re-scans on Shopify theme-editor section loads.
   Everything here is a progressive enhancement: with JS off, or
   with prefers-reduced-motion on, the page is fully readable.
   ============================================================ */
(function () {
  "use strict";
  if (window.__ecaInit) { window.__ecaScan && window.__ecaScan(); return; }
  window.__ecaInit = true;

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var fine   = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  var design = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- 1. scroll reveals ---------- */
  var io = null;
  if (!reduce && "IntersectionObserver" in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        var sibs = Array.prototype.filter.call(el.parentNode.children, function (n) {
          return n.classList && n.classList.contains("eca-rv");
        });
        var idx = sibs.indexOf(el);
        setTimeout(function () { el.classList.add("in"); }, Math.max(0, idx) * 70);
        io.unobserve(el);
      });
    }, { threshold: 0.15, rootMargin: "0px 0px -6% 0px" });
  }

  /* Marks the document so the CSS only hides things once JS is running —
     with JS off nothing is stuck at opacity 0. */
  if (!reduce) document.documentElement.classList.add("eca-anim");

  /* Anything inside a horizontally scrolling rail can sit outside the viewport
     forever, so its observer would never fire and the element would stay
     hidden. Those reveal immediately instead — never leave content invisible. */
  function inSideScroller(el) {
    for (var a = el.parentElement; a && a !== document.body; a = a.parentElement) {
      var ox = getComputedStyle(a).overflowX;
      if (ox === "auto" || ox === "scroll") return true;
    }
    return false;
  }

  function observe(el, cb) {
    if (!io2 || inSideScroller(el)) { cb(el); return; }
    el.__ecaCb = cb;
    io2.observe(el);
  }
  var io2 = null;
  if (!reduce && "IntersectionObserver" in window) {
    io2 = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        io2.unobserve(en.target);
        if (en.target.__ecaCb) en.target.__ecaCb(en.target);
      });
    }, { threshold: 0, rootMargin: "220px 0px -4% 0px" });
  }

  function scan() {
    var nodes = document.querySelectorAll(".eca-rv:not(.in):not([data-eca-seen])");
    Array.prototype.forEach.call(nodes, function (el) {
      el.setAttribute("data-eca-seen", "1");
      if (io) { io.observe(el); } else { el.classList.add("in"); }
    });
    counters();
    magnets();
    headings();
    rules();
    gridStagger();
    imageWipes();
  }
  window.__ecaScan = scan;

  /* ---------- 6. section headings reveal word by word ---------- */
  function headings() {
    if (reduce) return;
    var hs = document.querySelectorAll(".eca h2:not([data-eca-hsplit]):not([data-eca-split])");
    Array.prototype.forEach.call(hs, function (h) {
      h.setAttribute("data-eca-hsplit", "1");
      /* only plain-text headings — never rebuild markup that has children */
      if (h.children.length) return;
      var words = (h.textContent || "").trim().split(/\s+/);
      if (words.length < 1 || !words[0]) return;
      h.textContent = "";
      words.forEach(function (word, i) {
        var outer = document.createElement("span");
        outer.className = "eca-w";
        var inner = document.createElement("span");
        inner.className = "eca-wi";
        inner.textContent = word;
        inner.style.transitionDelay = (i * 0.055).toFixed(3) + "s";
        outer.appendChild(inner);
        h.appendChild(outer);
        if (i < words.length - 1) h.appendChild(document.createTextNode(" "));
      });
      h.classList.add("eca-hsplit");
      observe(h, function (el) { el.classList.add("is-in"); });
    });
  }

  /* ---------- 7. hairlines draw in from the left ---------- */
  function rules() {
    if (reduce) return;
    var rs = document.querySelectorAll(".eca-rule:not([data-eca-rule])");
    Array.prototype.forEach.call(rs, function (r) {
      r.setAttribute("data-eca-rule", "1");
      observe(r, function (el) { el.classList.add("in"); });
    });
  }

  /* ---------- 8. grid children cascade instead of landing together ---------- */
  var STAG = ".eca-grid, .eca-gal__grid, .eca-paths, .eca-quotes, .eca-rel__rail, .eca-pdp__trust";
  function gridStagger() {
    if (reduce) return;
    var grids = document.querySelectorAll(STAG);
    Array.prototype.forEach.call(grids, function (g) {
      if (g.getAttribute("data-eca-stag")) return;
      g.setAttribute("data-eca-stag", "1");
      var kids = Array.prototype.slice.call(g.children);
      kids.forEach(function (k, i) {
        k.classList.add("eca-stag");
        k.style.setProperty("--i", Math.min(i, 14));
      });
      observe(g, function () {
        kids.forEach(function (k) { k.classList.add("in"); });
      });
    });
  }

  /* ---------- 9. photos wipe up as they arrive ---------- */
  var WIPE = ".eca-gal__frame img, .eca-card__media img, .eca-rel__media img, .eca-coach__photo img";
  function imageWipes() {
    if (reduce) return;
    var imgs = document.querySelectorAll(WIPE);
    Array.prototype.forEach.call(imgs, function (im) {
      if (im.getAttribute("data-eca-wipe")) return;
      im.setAttribute("data-eca-wipe", "1");
      im.classList.add("eca-imgrv");
      observe(im, function (el) { el.classList.add("in"); });
    });
  }

  /* ---------- 2. count-up numbers ---------- */
  function counters() {
    if (reduce || !("IntersectionObserver" in window)) {
      Array.prototype.forEach.call(document.querySelectorAll("[data-eca-count]"), function (el) {
        el.textContent = el.getAttribute("data-eca-count");
      });
      return;
    }
    var els = document.querySelectorAll("[data-eca-count]:not([data-eca-counted])");
    if (!els.length) return;
    var co = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        var el = en.target;
        co.unobserve(el);
        var target = parseFloat(el.getAttribute("data-eca-count")) || 0;
        var suffix = el.getAttribute("data-eca-suffix") || "";
        var dur = 1100, t0 = null;
        function step(ts) {
          if (t0 === null) t0 = ts;
          var p = Math.min(1, (ts - t0) / dur);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased) + suffix;
          if (p < 1) requestAnimationFrame(step);
        }
        requestAnimationFrame(step);
      });
    }, { threshold: 0.5 });
    Array.prototype.forEach.call(els, function (el) {
      el.setAttribute("data-eca-counted", "1");
      el.textContent = "0" + (el.getAttribute("data-eca-suffix") || "");
      co.observe(el);
    });
  }

  /* ---------- 3. magnetic primary buttons ---------- */
  function magnets() {
    if (reduce || !fine) return;
    var btns = document.querySelectorAll(".eca-btn.solid:not([data-eca-magnet])");
    Array.prototype.forEach.call(btns, function (b) {
      b.setAttribute("data-eca-magnet", "1");
      var raf = null;
      b.addEventListener("pointermove", function (e) {
        var r = b.getBoundingClientRect();
        var dx = (e.clientX - (r.left + r.width / 2)) / r.width;
        var dy = (e.clientY - (r.top + r.height / 2)) / r.height;
        if (raf) return;
        raf = requestAnimationFrame(function () {
          raf = null;
          b.style.transform = "translate(" + (dx * 7).toFixed(1) + "px," + (dy * 5 - 2).toFixed(1) + "px)";
        });
      });
      b.addEventListener("pointerleave", function () {
        if (raf) { cancelAnimationFrame(raf); raf = null; }
        b.style.transform = "";
      });
    });
  }

  /* ---------- 4. first-load curtain ---------- */
  function curtain() {
    if (reduce || design) return;
    if (sessionStorage.getItem("ecaSeen") === "1") return;
    try { sessionStorage.setItem("ecaSeen", "1"); } catch (e) { /* private mode */ }

    var c = document.createElement("div");
    c.className = "eca-curtain";
    c.setAttribute("aria-hidden", "true");
    c.innerHTML = '<span class="eca-curtain__line"></span>';
    document.body.appendChild(c);
    requestAnimationFrame(function () {
      setTimeout(function () {
        c.classList.add("is-out");
        setTimeout(function () { c.remove(); }, 1000);
      }, 420);
    });
  }

  /* ---------- 5. anchor links ease instead of jump ---------- */
  function anchors() {
    document.addEventListener("click", function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute("href");
      if (!id || id === "#") return;
      var t = document.querySelector(id);
      if (!t) return;
      e.preventDefault();
      t.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    });
  }

  /* Safety net. A fast scroll or an anchor jump can move an element past the
     viewport between two frames, so its observer never fires and it would stay
     hidden for good. Anything at or above the fold that is still hidden gets
     revealed; only genuinely below-fold items keep waiting for their turn.
     Content is never left invisible. */
  function rescue() {
    var stuck = document.querySelectorAll(
      ".eca-stag:not(.in), .eca-imgrv:not(.in), .eca-rule:not(.in), .eca-hsplit:not(.is-in)"
    );
    if (!stuck.length) return;
    var vh = window.innerHeight;
    Array.prototype.forEach.call(stuck, function (el) {
      if (el.getBoundingClientRect().top < vh) {
        el.classList.add(el.classList.contains("eca-hsplit") ? "is-in" : "in");
      }
    });
  }

  function failsafe() {
    setTimeout(rescue, 2200);
    var t = null;
    window.addEventListener("scroll", function () {
      if (t) return;
      t = setTimeout(function () { t = null; rescue(); }, 400);
    }, { passive: true });
    window.addEventListener("pageshow", rescue);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { scan(); curtain(); anchors(); failsafe(); });
  } else {
    scan(); curtain(); anchors(); failsafe();
  }

  /* Shopify theme editor re-renders sections without a page load. */
  document.addEventListener("shopify:section:load", scan);
  document.addEventListener("shopify:section:select", scan);
})();
